/**
 * Account actions: change login e-mail (previous e-mails become aliases used
 * by the payment webhooks), and reset password (recovery link to the user's
 * inbox, or one-time temporary password that forces a change on next login).
 */

import React, { useState } from 'react';
import { Mail, KeyRound, Copy, Send, History, Trash2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { changeUserEmail, removeEmailAlias, resetUserPassword, type AdminUserDetail, type EmailAlias } from '@/services/adminService';
import { Badge, InfoRow, Section, formatDate, type useFeedback } from './shared';

const ALIAS_SOURCE_LABELS: Record<EmailAlias['source'], string> = {
  admin_email_change: 'Painel admin',
  support_email_change: 'API de suporte',
  backfill: 'Migração',
};

interface AccountPanelProps {
  detail: AdminUserDetail;
  feedback: ReturnType<typeof useFeedback>;
  onChanged: () => void;
}

const AccountPanel: React.FC<AccountPanelProps> = ({ detail, feedback, onChanged }) => {
  const userId = detail.auth.id;
  const aliases = detail.emailAliases ?? [];
  const [busy, setBusy] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    feedback.clear();
    try {
      await action();
    } catch (err) {
      feedback.error(err);
    } finally {
      setBusy(null);
    }
  };

  const handleChangeEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!confirm(`Trocar o e-mail de login de ${detail.auth.email} para ${email}? O e-mail antigo deixa de funcionar no login.`)) return;
    return run('email', async () => {
      const result = await changeUserEmail({ userId, newEmail: email });
      if (result.outcome === 'igual') feedback.success('O e-mail informado já é o atual.');
      else if (result.outcome === 'pendente_confirmacao') {
        feedback.success(`Troca para ${email} aguardando confirmação do usuário. O e-mail atual continua valendo até lá.`);
      } else {
        feedback.success(`E-mail alterado para ${email}. A senha continua a mesma; o e-mail anterior foi salvo para vincular compras.`);
      }
      setNewEmail('');
      onChanged();
    });
  };

  const handleRemoveAlias = (alias: EmailAlias) => {
    if (!confirm(`Remover ${alias.email} dos e-mails anteriores? Compras futuras com esse e-mail deixam de ser vinculadas a esta conta.`)) return;
    return run(`alias:${alias.email}`, async () => {
      await removeEmailAlias({ userId, email: alias.email });
      feedback.success(`${alias.email} removido dos e-mails anteriores.`);
      onChanged();
    });
  };

  const handleSendLink = () => {
    if (!confirm(`Enviar link de redefinição de senha para ${detail.auth.email}?`)) return;
    return run('link', async () => {
      await resetUserPassword({ userId, mode: 'link' });
      feedback.success(`Link enviado para ${detail.auth.email} (válido por 60 min).`);
      onChanged();
    });
  };

  const handleTemporary = () => {
    if (!confirm('Gerar uma senha temporária? A senha atual do usuário deixa de funcionar imediatamente.')) return;
    return run('temporary', async () => {
      const result = await resetUserPassword({ userId, mode: 'temporary' });
      if (result.mode === 'temporary') setTemporaryPassword(result.temporaryPassword);
      feedback.success(
        'Senha temporária gerada. Ela só é exibida agora — copie e envie ao usuário por um canal seguro. O usuário será obrigado a trocar a senha no próximo login.',
      );
      onChanged();
    });
  };

  return (
    <div className="space-y-4">
      <Section title="E-mail de login" icon={<Mail className="h-4 w-4 text-muted-foreground" />}>
        <InfoRow label="Atual" value={detail.auth.email ?? '—'} />
        <InfoRow label="Confirmado em" value={formatDate(detail.auth.email_confirmed_at)} />
        <InfoRow label="Último login" value={formatDate(detail.auth.last_sign_in_at)} />
        <div className="flex items-end gap-2 mt-3">
          <div className="space-y-1 flex-1">
            <Label className="text-xs text-muted-foreground">Novo e-mail</Label>
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="novo@email.com" />
          </div>
          <Button size="sm" variant="outline" onClick={handleChangeEmail} disabled={!newEmail.includes('@') || !!busy}>
            {busy === 'email' ? 'Trocando…' : 'Trocar e-mail'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          O e-mail atual passa a ser um e-mail anterior: compras feitas com ele continuam vinculadas a esta conta.
        </p>
      </Section>

      <Section title="E-mails anteriores" icon={<History className="h-4 w-4 text-muted-foreground" />}>
        {aliases.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum e-mail anterior.</p>
        ) : (
          <ul className="divide-y divide-border">
            {aliases.map((alias) => (
              <li key={alias.email} className="flex items-center gap-3 py-2 text-sm">
                <span className="font-medium text-foreground break-all">{alias.email}</span>
                <Badge label={ALIAS_SOURCE_LABELS[alias.source] ?? alias.source} />
                <span className="text-xs text-muted-foreground ml-auto shrink-0">{formatDate(alias.createdAt)}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveAlias(alias)}
                  disabled={!!busy}
                  aria-label={`Remover ${alias.email}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> {busy === `alias:${alias.email}` ? 'Removendo…' : 'Remover'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-muted-foreground mt-2">Compras feitas com esses e-mails são vinculadas a esta conta.</p>
      </Section>

      <Section title="Senha" icon={<KeyRound className="h-4 w-4 text-muted-foreground" />}>
        {detail.auth.must_change_password && (
          <div className="mb-3 flex items-center gap-2 text-sm bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 p-2 rounded-lg">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <span>Troca de senha pendente: o usuário precisa definir uma nova senha antes de usar a plataforma.</span>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={handleSendLink} disabled={!!busy}>
            <Send className="h-4 w-4" /> {busy === 'link' ? 'Enviando…' : 'Enviar link de redefinição'}
          </Button>
          <Button size="sm" variant="destructive" onClick={handleTemporary} disabled={!!busy}>
            <KeyRound className="h-4 w-4" /> {busy === 'temporary' ? 'Gerando…' : 'Gerar senha temporária'}
          </Button>
        </div>
        {temporaryPassword && (
          <div className="mt-3 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 text-sm p-3 rounded-lg flex items-center gap-2">
            <span>
              Senha temporária: <code className="font-mono bg-muted px-1.5 py-0.5 rounded">{temporaryPassword}</code>
            </span>
            <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(temporaryPassword)} aria-label="Copiar senha">
              <Copy className="h-3 w-3" />
            </Button>
          </div>
        )}
      </Section>
    </div>
  );
};

export default AccountPanel;
